import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { getDaysFromDateRange } from 'utils/dateTime'
import moment from 'moment'
import classNames from 'classnames/bind'
import { MetricHeader } from './components/metric-header'
import { DateHeader } from './components/date-header'
import { DateCell } from './components/date-cell'
import { MetricCell } from './components/metric-cell'
import { DateFooter } from './components/date-footer'
import { MetricFooter } from './components/metric-footer'
import styles from './DataTable.module.scss'
import { KeyPerformanceMetrics } from 'utils/constants'

const cx = classNames.bind(styles)

export const calculatePeriod = (t, period1, period2) => {
  const days1 = getDaysFromDateRange(period1)
  const days2 = getDaysFromDateRange(period2)

  const isYesterday = days1.to === -1
  if (isYesterday) {
    const fromDays1 = -days1.from
    const fromDays2 = -days2.from
    const toDays2 = -days2.to

    if (period2) {
      if ((fromDays2 - toDays2) >= 28 && (fromDays2 - toDays2) <= 31) {
        return 'month'
      } else if ((fromDays2 - toDays2) >= 90 && (fromDays2 - toDays2) <= 92) {
        return 'quarter'
      } else if ((fromDays2 - toDays2) >= 364 && (fromDays2 - toDays2) <= 366) {
        return 'year'
      }
    }

    if (fromDays1 === 1) {
      return `${t('widgets.performanceReport.day')}`
    } else if (fromDays1 <= 7) {
      return `${fromDays1} ${t('widgets.performanceReport.days')}`
    } else if (fromDays1 % 7 === 0) {
      return `${fromDays1 / 7} ${t('widgets.performanceReport.weeks')}`
    } else {
      return `${fromDays1} ${t('widgets.performanceReport.days')}`
    }
  } else {
    return null
  }
}

const DataTable = ({
  data,
  metrics,
  metricType,
  metricTypes,
  headerColours,
  highlight,
  previousColumn,
  period1,
  period2,
  analytics,
  hideFooter,
  stickyColumns = [],
  alternatingRows = false,
  enableColors = true,
  highlightWeekend = true,
  nextDayDelimiter = false,
  activeTab = null,
  dateFormat,
}) => {
  const { t } = useTranslation(['translations'])

  const isHighlight = item => {
    if (!highlight) {
      return false
    }
    return moment.utc(highlight.date).isSame(moment.utc(item.date), 'day')
  }

  const isWeekend = date => {
    if (!highlightWeekend) {
      return false
    }
    const weekday = moment.utc(date).weekday()
    if (weekday === 0) {
      return true
    } else if (weekday === 6) {
      return true
    }
    return false
  }

  const periodString = calculatePeriod(t, period1, period2)
  const colour = enableColors ? ['green', 'purple', 'blue'] : ['grey']
  const stickyConfigMap = {}
  stickyColumns.forEach((config, index) => {
    const isLast = index === stickyColumns.length - 1
    if (config.left !== undefined) {
      // If left position is explicitly provided, use it
      stickyConfigMap[config.metric] = {
        width: config.width || 240,
        left: config.left,
        zIndex: config.zIndex || 1,
        isLast
      }
    } else {
      // Calculate left position automatically based on previous columns
      // First column starts at 0 (relative to scrollable container)
      let calculatedLeft = 0
      // Sum up widths of all previous sticky columns
      for (let i = 0; i < index; i++) {
        calculatedLeft += stickyColumns[i].width || (i === 0 ? 240 : 150)
      }
      stickyConfigMap[config.metric] = {
        width: config.width || (index === 0 ? 240 : 150),
        left: calculatedLeft,
        zIndex: config.zIndex || 1,
        isLast
      }
    }
  })
  let colorIndex = 0
  const getStickyConfig = (metric) => {
    return stickyConfigMap[metric] || null
  }

  return (
    <table className={cx('table', { 'key-performance-metrics': activeTab === KeyPerformanceMetrics })} data-testid="data-table">
      <thead>
        <tr>
          <DateHeader
            stickyConfig={getStickyConfig('Date')}
            activeTab={activeTab}
          />
          {metrics.map((metric, index) => {
            const stickyConfig = getStickyConfig(metric)
            const headerColour = stickyConfig
              ? 'grey'
              : colour[colorIndex++ % colour.length]
            return (
              <MetricHeader
                key={metric}
                metric={metric}
                periodString={periodString}
                previousColumn={previousColumn}
                analytics={analytics}
                headerColour={headerColour}
                customColour={headerColours?.[index]}
                stickyConfig={stickyConfig}
                activeTab={activeTab}
              />
            )
          })}
        </tr>
      </thead>
      <tbody>
        {data[0]?.map((item, i) => {
          const previousItem = i > 0 && data[0]?.[i - 1]
          const isNextDayDelimiter = nextDayDelimiter &&
            i > 0 &&
            i !== data[0].length &&
            previousItem &&
            moment(item.date).format('YYYY-MM-DD') !== moment(previousItem.date).format('YYYY-MM-DD')
          return (
          <tr
            key={`${item}_${i}`}
            className={cx({
              highlight: isHighlight(item),
              weekend: isWeekend(item.date),
              'row-even': alternatingRows && i % 2 === 0,
              'row-odd': alternatingRows && i % 2 === 1,
              'next-day-delimiter': isNextDayDelimiter
            })}
          >
            <DateCell
              item={item}
              previousColumn={previousColumn}
              rowOdd={alternatingRows && i % 2 === 1}
              stickyConfig={getStickyConfig('Date')}
              dateFormat={dateFormat}
            />
            {data.map((forDate, metricIndex) => {
              const metricData = forDate[i]
              const columnMetricType = metricTypes && metricTypes[metricIndex] ? metricTypes[metricIndex] : metricType
              const metric = metrics[metricIndex]
              const stickyConfig = getStickyConfig(metric)
              return (
                <MetricCell
                  key={`${metricIndex}_${i}`}
                  metricData={metricData}
                  metricType={columnMetricType}
                  previousColumn={previousColumn}
                  value={metricData.value}
                  analytics={analytics}
                  stickyConfig={stickyConfig}
                  rowEven={alternatingRows && i % 2 === 0}
                  rowOdd={alternatingRows && i % 2 === 1}
                  isHighlight={isHighlight(item)}
                />
              )
            })}
          </tr>
          )
        })}
      </tbody>
      {!hideFooter && (
        <tfoot>
          <tr>
            <DateFooter metricType={metricType} />
            {metrics.map((metric, i) => {
              const metricData = data[i] ?? []
              const columnMetricType = metricTypes && metricTypes[i] ? metricTypes[i] : metricType
              return (
                <MetricFooter
                  key={metric}
                  metricType={columnMetricType}
                  previousColumn={previousColumn}
                  metricData={metricData}
                />
              )
            })}
          </tr>
        </tfoot>
      )}
    </table>
  )
}

DataTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        value: PropTypes.number,
        previousValue: PropTypes.number,
      })
    )
  ),
  metrics: PropTypes.arrayOf(PropTypes.string),
  metricType: PropTypes.string,
  metricTypes: PropTypes.arrayOf(PropTypes.string),
  headerColours: PropTypes.arrayOf(PropTypes.string),
  highlight: PropTypes.shape({
    date: PropTypes.instanceOf(Date).isRequired,
  }),
  previousColumn: PropTypes.bool,
  period1: PropTypes.shape({
    from: PropTypes.instanceOf(moment),
    to: PropTypes.instanceOf(moment),
  }),
  period2: PropTypes.shape({
    from: PropTypes.instanceOf(moment),
    to: PropTypes.instanceOf(moment),
  }),
  analytics: PropTypes.bool,
  hideFooter: PropTypes.bool,
  stickyColumns: PropTypes.arrayOf(
    PropTypes.shape({
      metric: PropTypes.string.isRequired,
      width: PropTypes.number,
      left: PropTypes.number,
      headerColour: PropTypes.string,
      zIndex: PropTypes.number
    })
  ),
  alternatingRows: PropTypes.bool,
  enableColors: PropTypes.bool,
  highlightWeekend: PropTypes.bool,
  nextDayDelimiter: PropTypes.bool,
  activeTab: PropTypes.string,
  dateFormat: PropTypes.string
}

DataTable.defaultProps = {
  data: null,
  label: null,
  metric: null,
  highlight: null,
  previousLine: false,
  period1: null,
  period2: null,
  analytics: false,
  hideFooter: false,
  stickyColumns: [],
  alternatingRows: false,
  enableColors: true,
  highlightWeekend: true,
  nextDayDelimiter: false,
  activeTab: null,
  dateFormat: null
}

export { DataTable }
